import { useEffect, useState } from 'react'

// PublishCelebration — plays on successful publish
// Doodle-style glasses clink, title fades in, CI earned, then fades out
// Props: title, ciEarned, newRank, onDone (called when animation finishes)

export default function PublishCelebration({ title, ciEarned = 0, newRank = null, onDone }) {
  const [phase, setPhase] = useState('enter')   // enter → clink → reveal → exit
  const [clinkDone, setClinkDone] = useState(false)

  useEffect(() => {
    // Phase timeline
    const t1 = setTimeout(() => setPhase('clink'), 300)
    const t2 = setTimeout(() => setClinkDone(true), 1100)
    const t3 = setTimeout(() => setPhase('reveal'), 1200)
    const t4 = setTimeout(() => setPhase('exit'), 3800)
    const t5 = setTimeout(() => onDone?.(), 4400)
    return () => [t1, t2, t3, t4, t5].forEach(clearTimeout)
  }, [])

  return (
    <div style={{
      ...s.overlay,
      opacity: phase === 'exit' ? 0 : 1,
      transition: phase === 'exit' ? 'opacity 0.6s ease' : 'opacity 0.4s ease',
    }}>
      <div style={{
        ...s.card,
        transform: phase === 'enter' ? 'translateY(20px) scale(0.97)' : 'translateY(0) scale(1)',
        opacity: phase === 'enter' ? 0 : 1,
        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>

        {/* Doodle glasses SVG */}
        <div style={s.glassesWrap}>
          <DoodleGlasses clinking={phase === 'clink' || clinkDone} />
        </div>

        {/* Title reveal */}
        <div style={{
          ...s.titleWrap,
          opacity: phase === 'reveal' || phase === 'exit' ? 1 : 0,
          transform: phase === 'reveal' || phase === 'exit' ? 'translateY(0)' : 'translateY(8px)',
          transition: 'all 0.5s ease 0.1s',
        }}>
          <p style={s.publishedLabel}>published</p>
          <h2 style={s.title}>{title}</h2>
        </div>

        {/* CI earned */}
        {ciEarned > 0 && (
          <div style={{
            ...s.ciRow,
            opacity: phase === 'reveal' || phase === 'exit' ? 1 : 0,
            transform: phase === 'reveal' || phase === 'exit' ? 'translateY(0)' : 'translateY(6px)',
            transition: 'all 0.5s ease 0.3s',
          }}>
            <span style={s.ciPlus}>+{ciEarned}</span>
            <span style={s.ciLabel}>column inches</span>
            {newRank && (
              <>
                <span style={s.ciDot}>·</span>
                <span style={s.rankLabel}>↑ {newRank}</span>
              </>
            )}
          </div>
        )}

        {/* Tagline */}
        <p style={{
          ...s.tagline,
          opacity: phase === 'reveal' || phase === 'exit' ? 1 : 0,
          transition: 'opacity 0.5s ease 0.5s',
        }}>
          say something real.
        </p>
      </div>
    </div>
  )
}

// Doodle-style glasses that animate a clink
function DoodleGlasses({ clinking }) {
  return (
    <svg
      viewBox="0 0 160 80"
      width="160"
      height="80"
      style={{
        overflow: 'visible',
        filter: 'drop-shadow(0 2px 8px rgba(200,64,26,0.2))',
      }}
    >
      {/* Left glass */}
      <g style={{
        transform: clinking ? 'translate(-4px, -3px) rotate(-8deg)' : 'translate(0,0) rotate(0deg)',
        transformOrigin: '52px 40px',
        transition: 'transform 0.25s cubic-bezier(0.34, 1.8, 0.64, 1)',
      }}>
        {/* Glass body */}
        <path d="M 20 55 Q 18 25 35 18 Q 52 12 58 30 Q 64 48 52 58 Q 38 68 20 55 Z"
          fill="none" stroke="#C8401A" strokeWidth="2.5" strokeLinejoin="round"
          style={{ strokeDasharray: '200', strokeDashoffset: clinking ? '0' : '200', transition: 'stroke-dashoffset 0.6s ease' }}
        />
        {/* Liquid */}
        <path d="M 24 52 Q 22 35 36 26 Q 50 18 55 34 Q 46 50 30 56 Z"
          fill="rgba(200,64,26,0.12)"
          style={{ opacity: clinking ? 1 : 0, transition: 'opacity 0.4s ease 0.3s' }}
        />
        {/* Bubbles */}
        {clinking && <>
          <circle cx="38" cy="38" r="2" fill="rgba(200,64,26,0.4)" style={{ animation: 'bubble-rise 1s ease infinite 0.1s' }} />
          <circle cx="44" cy="32" r="1.5" fill="rgba(200,64,26,0.3)" style={{ animation: 'bubble-rise 1.2s ease infinite 0.3s' }} />
        </>}
        {/* Stem */}
        <line x1="40" y1="62" x2="40" y2="76" stroke="#C8401A" strokeWidth="2.5" strokeLinecap="round"
          style={{ strokeDasharray: '20', strokeDashoffset: clinking ? '0' : '20', transition: 'stroke-dashoffset 0.4s ease 0.2s' }}
        />
        {/* Base */}
        <line x1="30" y1="76" x2="50" y2="76" stroke="#C8401A" strokeWidth="2.5" strokeLinecap="round"
          style={{ strokeDasharray: '24', strokeDashoffset: clinking ? '0' : '24', transition: 'stroke-dashoffset 0.4s ease 0.3s' }}
        />
      </g>

      {/* Right glass */}
      <g style={{
        transform: clinking ? 'translate(4px, -3px) rotate(8deg)' : 'translate(0,0) rotate(0deg)',
        transformOrigin: '108px 40px',
        transition: 'transform 0.25s cubic-bezier(0.34, 1.8, 0.64, 1)',
      }}>
        <path d="M 140 55 Q 142 25 125 18 Q 108 12 102 30 Q 96 48 108 58 Q 122 68 140 55 Z"
          fill="none" stroke="#C8401A" strokeWidth="2.5" strokeLinejoin="round"
          style={{ strokeDasharray: '200', strokeDashoffset: clinking ? '0' : '200', transition: 'stroke-dashoffset 0.6s ease 0.1s' }}
        />
        <path d="M 136 52 Q 138 35 124 26 Q 110 18 105 34 Q 114 50 130 56 Z"
          fill="rgba(200,64,26,0.12)"
          style={{ opacity: clinking ? 1 : 0, transition: 'opacity 0.4s ease 0.3s' }}
        />
        {clinking && <>
          <circle cx="122" cy="38" r="2" fill="rgba(200,64,26,0.4)" style={{ animation: 'bubble-rise 1.1s ease infinite 0.2s' }} />
          <circle cx="116" cy="30" r="1.5" fill="rgba(200,64,26,0.3)" style={{ animation: 'bubble-rise 0.9s ease infinite 0.4s' }} />
        </>}
        <line x1="120" y1="62" x2="120" y2="76" stroke="#C8401A" strokeWidth="2.5" strokeLinecap="round"
          style={{ strokeDasharray: '20', strokeDashoffset: clinking ? '0' : '20', transition: 'stroke-dashoffset 0.4s ease 0.25s' }}
        />
        <line x1="110" y1="76" x2="130" y2="76" stroke="#C8401A" strokeWidth="2.5" strokeLinecap="round"
          style={{ strokeDasharray: '24', strokeDashoffset: clinking ? '0' : '24', transition: 'stroke-dashoffset 0.4s ease 0.35s' }}
        />
      </g>

      {/* Clink sparkles */}
      {clinking && (
        <g style={{ animation: 'sparkle-pop 0.6s ease forwards' }}>
          <line x1="78" y1="18" x2="82" y2="8"  stroke="#C8401A" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="80" y1="16" x2="72" y2="10" stroke="#C8401A" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="80" y1="16" x2="88" y2="10" stroke="#C8401A" strokeWidth="1.5" strokeLinecap="round" />
          <circle cx="80" cy="16" r="2.5" fill="#C8401A" />
        </g>
      )}

      <style>{`
        @keyframes bubble-rise {
          0%   { transform: translateY(0) scale(1); opacity: 0.6; }
          100% { transform: translateY(-12px) scale(0.5); opacity: 0; }
        }
        @keyframes sparkle-pop {
          0%   { opacity: 0; transform: scale(0.5); }
          40%  { opacity: 1; transform: scale(1.2); }
          100% { opacity: 0; transform: scale(1); }
        }
      `}</style>
    </svg>
  )
}

const s = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(13,11,10,0.88)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)',
  },
  card: {
    background: 'var(--paper)',
    border: '1px solid var(--rule)',
    padding: '3rem 3.5rem',
    maxWidth: 480,
    width: '90%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.25rem',
  },
  glassesWrap: {
    marginBottom: '0.5rem',
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
  },
  publishedLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
  },
  title: {
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    color: 'var(--ink)',
    maxWidth: '36ch',
  },
  ciRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    border: '1px solid rgba(200,64,26,0.25)',
    background: 'rgba(200,64,26,0.06)',
  },
  ciPlus: {
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#C8401A',
  },
  ciLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
  },
  ciDot: {
    color: 'var(--rule)',
  },
  rankLabel: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--accent)',
  },
  tagline: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.6rem',
    letterSpacing: '0.12em',
    color: 'var(--muted)',
    fontStyle: 'italic',
  },
}
